import { Button, Form, Input, Select, Skeleton, Space, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ModelSelectOption from "@/components/Elements/ModelSelectOption";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { useCreate } from "@/hooks/queries";
import { AssetTypesSelect } from "@/modules/asset-types/asset-types-select";
import { validationErrorParser } from "@/modules/form-validation";
import SelectLangField from "@/modules/language/language-select-lang-field";
import { useModelNavigate } from "@/modules/model-navigate";
import {
  ModulesSelect,
  useDefaultModulesSelectOptionValues,
} from "@/modules/modules/modules-select";
import QueryableSelect from "@/modules/queryable-select";
import { getSelectMultipleValues } from "@/modules/select";
import { SidePanelFooter, useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";
import { createRandomId } from "@/utilities/id";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm } = Form;
const { Title } = Typography;

const Create = ({ formId }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { navigateToModel } = useModelNavigate();
  const { closeSidePanel } = useSidePanel();

  const mutation = useCreate("interventionTemplates", { rootId: formId });

  const { types: interventionTypes } = useInterventionType();

  const { defaultModulesSelectValues, isLoading: isDefaultModulesLoading } =
    useDefaultModulesSelectOptionValues();

  const initialValues = {
    modules: defaultModulesSelectValues,
  };

  if (isDefaultModulesLoading) {
    return <Skeleton active />;
  }

  const onClose = () => {
    form.resetFields();
    closeSidePanel();
  };

  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(
          mutation.error,
          [
            "name",
            "lang",
            "description",
            "intervention_type_id",
            "organizations",
            "asset_types",
            "modules",
          ],
          "others"
        )
      : {};
  }

  function validation(item) {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  }

  const onCreate = () => {
    const data = {
      name: form.getFieldValue("name"),
      lang: form.getFieldValue("language"),
      description: form.getFieldValue("description"),
      intervention_type_id: form.getFieldValue("type"),
      organizations: getSelectMultipleValues(form, "organizations", []),
      asset_types: getSelectMultipleValues(form, "asset_types", []),
      modules: getSelectMultipleValues(form, "modules", []),
      layout_index: createRandomId(),
    };

    mutation.mutate(data, {
      onSuccess: function (response) {
        onClose();
        const id = response?.data?.data?.id;
        navigateToModel("interventiontemplate", { id });
      },
    });
  };

  const FORM_ID = "createInterventionTemplateForm";

  return (
    <>
      <Form
        id={FORM_ID}
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={onCreate}
      >
        <Title level={3}>{t("interventionTemplate:informations.label")}</Title>
        <Form.Item
          name="name"
          label={t("interventionTemplate:form.name.label")}
          rules={[{ required: true }]}
          {...validation("name")}
        >
          <Input
            placeholder={t("interventionTemplate:form.name.label")}
            data-testid="name"
          />
        </Form.Item>
        <SelectLangField />
        <Form.Item
          name="description"
          label={t("interventionTemplate:form.description.label")}
        >
          <Input
            placeholder={t("interventionTemplate:form.description.label")}
            data-testid="description"
          />
        </Form.Item>
        <Form.Item
          name="type"
          label={t("interventionTemplate:form.type.label")}
          rules={[{ required: true }]}
          {...validation("type")}
        >
          <Select
            placeholder={t("interventionTemplate:form.type.label")}
            data-testid="type"
          >
            {interventionTypes.map(({ id, name }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <QueryableSelect
          model="organizations"
          filterBy="name"
          fields={["id", "name"]}
          mode="multiple"
          optionFormat={ModelSelectOption}
          name="organizations"
          label={t("interventionTemplate:form.organizations.label")}
          placeholder={t("interventionTemplate:form.organizations.label")}
          {...validation("organizations")}
        />
        <AssetTypesSelect {...validation("asset_types")} />
        <ModulesSelect {...validation("modules")} />

        {validationErrors().others && <Form.Item {...validation("others")} />}
        <Alert
          display={mutation.isError}
          closable={{ afterClose: () => mutation.reset() }}
        />
        <SidePanelFooter>
          <Space>
            <Button onClick={onClose}>{t("global:cancel")}</Button>
            <Button
              loading={mutation.isPending}
              type="primary"
              data-testid="create"
              htmlType="submit"
              form={FORM_ID}
            >
              {t("global:add")}
            </Button>
          </Space>
        </SidePanelFooter>
      </Form>
    </>
  );
};

Create.propTypes = {
  formId: PropTypes.string,
};

export default Create;
